import { useEffect, useMemo, useRef, useState } from 'react';
import { configVars } from 'src/helpers';
import { IValueLabel } from 'src/ts/interfaces';

interface UseGooglePlacesAutocompleteProps {
  inputValue: string;
  options?: {
    types?: string[];
    componentRestrictions?: { country: string };
    fields?: string[];
  };
  debounceTime?: number;
}

interface SelectedPlaceDetails {
  placeId: PlaceId;
  address?: Address;
}

type PlaceId = string;

interface UseGooglePlacesAutocompleteResult {
  placeSuggestions: google.maps.places.AutocompleteSuggestion[];
  isLoading: boolean;
  error: string | null;
  formatPlacesSuggestions: (
    suggestions: google.maps.places.AutocompleteSuggestion[]
  ) => IValueLabel[];
  setSelectedPlace: (place: SelectedPlaceDetails | null) => void;
  selectedPlace: SelectedPlaceDetails | null;
}

function areSuggestionsEqual(
  previousSuggestions: google.maps.places.AutocompleteSuggestion[] | null,
  currentSuggestions: google.maps.places.AutocompleteSuggestion[]
) {
  return JSON.stringify(previousSuggestions) === JSON.stringify(currentSuggestions);
}

/**
 * Method for formatting the suggestions from the Google Places API
 *
 * @param suggestions
 */
function formatPlacesSuggestions(
  suggestions: google.maps.places.AutocompleteSuggestion[]
): IValueLabel[] {
  return suggestions.map((suggestion, i) => {
    const place = suggestion.placePrediction;
    return {
      label: place?.text?.text,
      value: place?.mainText?.text,
      metadata: {
        placeId: place?.placeId
      }
    } as IValueLabel;
  });
}

interface Address {
  zipCode: string;
  city: string;
  state: string;
  country: string;
  street: string;
}

function formatAddressData(
  addressComponents: google.maps.places.AddressComponent[] | undefined
): Address | undefined {
  if (!addressComponents) return undefined;
  const streetNumber = addressComponents.find((component) =>
    component.types?.includes('street_number')
  );
  const streetName = addressComponents.find((component) => component.types?.includes('route'));
  const city = addressComponents.find((component) => component.types?.includes('locality'));
  const state = addressComponents.find((component) =>
    component.types?.includes('administrative_area_level_1')
  );
  const country = addressComponents.find((component) => component.types?.includes('country'));
  const postalCode = addressComponents.find((component) =>
    component.types?.includes('postal_code')
  );
  return {
    zipCode: postalCode?.longText || '',
    city: city?.longText || '',
    state: state?.shortText || '',
    country: country?.longText || '',
    street: `${streetNumber?.longText || ''} ${streetName?.longText || ''}`
  } as Address;
}

export const loadGoogleMapsAPI = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve(); // Google Maps API is already loaded
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps API'));
    document.head.appendChild(script);
  });
};

const useGooglePlacesAutocomplete = ({
  inputValue,
  options = {},
  debounceTime = 1000
}: UseGooglePlacesAutocompleteProps): UseGooglePlacesAutocompleteResult => {
  const [placeSuggestions, setPlaceSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const inputValueRef = useRef<string>(inputValue);

  const [selectedPlace, setSelectedPlace] = useState<SelectedPlaceDetails | null>(null);
  const selectedPlaceRef = useRef<string | null>(null);

  const isGoogleMapsAPILoaded = window?.google && window?.google?.maps;

  const previousSuggestions = useRef<google.maps.places.AutocompleteSuggestion[] | null>(null);

  // Load the Google Maps API and initialize services
  useEffect(() => {
    if (isGoogleMapsAPILoaded) {
      return; // If the autocomplete service is already initialized, do nothing
    }
    const initializeServices = async () => {
      try {
        await loadGoogleMapsAPI(configVars.google.token);
      } catch (err) {
        console.error(err);
        setError('Failed to load Google Maps API');
      }
    };

    initializeServices();
  }, [isGoogleMapsAPILoaded]);

  // Fetch autocomplete predictions when input changes
  useEffect(() => {
    if (!isGoogleMapsAPILoaded) {
      // If the autocomplete service is not initialized, do not fetch predictions
      return;
    }
    if (!inputValue.trim()) {
      // If the input value is empty, clear suggestions
      setSelectedPlace(null);
      if (placeSuggestions.length > 0) setPlaceSuggestions([]);
      return;
    }
    // The only time we want this useEffect hook to run is if the inputValue changes
    if (inputValueRef.current === inputValue) {
      return;
    } else if (inputValueRef.current !== inputValue) {
      inputValueRef.current = inputValue;
    }

    const fetchPredictions = async () => {
      setIsLoading(true);
      setError(null);
      setSelectedPlace(null);

      try {
        const response =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: inputValue,
            ...options
          });
        if (!areSuggestionsEqual(previousSuggestions.current, response.suggestions)) {
          setPlaceSuggestions(response.suggestions || []);
          previousSuggestions.current = response.suggestions || [];
        }
      } catch (err) {
        console.error(err);
        setError('Failed to fetch autocomplete suggestions');
        if (placeSuggestions.length > 0) setPlaceSuggestions([]);
        previousSuggestions.current = [];
      } finally {
        setIsLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchPredictions, debounceTime);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [inputValue, options, debounceTime, isGoogleMapsAPILoaded, placeSuggestions.length]);

  useEffect(() => {
    if (!selectedPlace) return;
    if (selectedPlaceRef.current === selectedPlace.placeId) {
      return;
    } else if (selectedPlaceRef.current !== selectedPlace.placeId) {
      selectedPlaceRef.current = selectedPlace.placeId;
    }
    // find the selected place that matches the suggestions
    const selectedPlaceSuggestion = placeSuggestions.find(
      (place) => place.placePrediction?.placeId === selectedPlace.placeId
    );

    const place = selectedPlaceSuggestion?.placePrediction?.toPlace();

    const fetchPlaceDetails = async () => {
      try {
        const response = await place?.fetchFields({
          fields: ['addressComponents', 'formattedAddress']
        });
        const address = formatAddressData(response?.place.addressComponents);
        setSelectedPlace((prev) => ({
          placeId: prev?.placeId as string,
          address: address
        }));
      } catch (err) {
        console.error(err);
        setError('Failed to fetch place details');
      }
    };
    const timeoutId = setTimeout(fetchPlaceDetails, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  });

  const result = useMemo(
    () => ({
      placeSuggestions,
      isLoading,
      error,
      formatPlacesSuggestions,
      selectedPlace,
      setSelectedPlace
    }),
    [placeSuggestions, isLoading, error, selectedPlace]
  );
  return result;
};

export default useGooglePlacesAutocomplete;
